import React from 'react';
import Dropzone from 'react-dropzone';
import api from '../../api';
import './Upload.scss';

import uploadedIcon from './file-upload-done.svg';
import Button from '../button';
import { MediaFile } from '../../core';
import classnames from 'classnames';

interface CustomProps {
  name: string;
  id: string;
  onFileChosen?: (file?: MediaFile) => void;
  disabled?: boolean;
  title?: string;
  description?: string;
  accept?: string[];
  value?: MediaFile;
  className?: string;
  checkDimensions?: boolean; // New prop to enable/disable dimension checking
}

const FileUpload: React.SFC<CustomProps> = (props: CustomProps) => {
  const [imageFile, setImage] = React.useState(props.value?.originalname);
  const [mediaFile, setFile] = React.useState<MediaFile>();
  const [progress, setProgress] = React.useState(0);
  const [isUploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string>('');

  const validateImageDimensions = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const width = img.width;
        const height = img.height;
        URL.revokeObjectURL(img.src);
        
        // if ((width > 1200 || height > 800) ) {
        //   setError(`Image dimensions must not exceed 1200px × 800px. Current size: ${width}px × ${height}px`);
        //   resolve(false);
        // }else
         if(width < 1200 || height < 800){
          setError(`Image dimensions must be at least 1200px × 800px. Current size: ${width}px × ${height}px`);
          resolve(false);
        }else {
          setError('');
          resolve(true);
        }
      };
    });
  };

  const handleDrop = async (selectedFiles: File[]) => {
    if (selectedFiles && selectedFiles.length > 0) {
      const file = selectedFiles[0];
      setError(''); // Clear any previous errors
      
      // Check dimensions only if checkDimensions is true and it's an image file
      if (props.checkDimensions && file.type.startsWith('image/')) {
        const isValidDimensions = await validateImageDimensions(file);
        if (!isValidDimensions) {
          return;
        }
      }

      setUploading(true);
      const uploadedResponse = await api.upload.upload(file, onProgress);
      if (props.onFileChosen) {
        props.onFileChosen(uploadedResponse?.data);
        setFile(uploadedResponse?.data);
      }
      setImage(URL.createObjectURL(file));
      setUploading(false);
    }
  };

  React.useEffect(() => {
    if (props.value) {
      setImage(props.value?.originalname);
      setFile(props.value);
    }
  }, [props.value]);

  const onProgress = (event: ProgressEvent) => {
    const progressPercentage = event.loaded / event.total;

    setProgress(progressPercentage);
  };

  const closeFile = () => {
    setUploading(false);
    setError('');
    if (props.onFileChosen) {
      props.onFileChosen();
    }
    setImage('');
  };

  const isChosen = !!imageFile;

  if (!isChosen) {
    return (
      <>
        <Dropzone
          accept={props.accept || undefined}
          onDrop={handleDrop}
          multiple={false}
        >
          {({ getRootProps, getInputProps }) => (
            <div
              {...getRootProps({
                className: 'drag-drop-file',
              })}
            >
              <input {...getInputProps()} className="d-none" />
              <label
                htmlFor="upload"
                className={classnames({
                  'upload p-3 rounded text-center font-normal uploading w-100 m-0 cursor-pointer d-flex align-items-center justify-content-center': true,
                  [props.className || '']: true,
                })}
              >
                <span className="d-block">
                  <i className="icon-upload font-xxl"></i>
                  <span className="default-upload-label">
                    <span className="d-block font-weight-semibold mt-2 py-1">
                      {props.title}
                    </span>
                    <span className="d-block text-gray-500 text-decoration-underline">
                      Drag the file here. Or Click here
                    </span>
                    {props.checkDimensions && (
                      <>
                      {/* <span className="d-block text-gray-500 mt-1">
                        Maximum dimensions: 1200px × 800px
                      </span> */}
                      <span className="d-block text-gray-500 mt-1">
                        Minimum dimensions: 1200px (Height) × 800px (width)
                      </span>
                      <span className="d-block text-gray-500 mt-1">
                        Max file size : 50mb
                      </span>
                      <span className="d-block text-gray-500 mt-1">
                        Supported file types : jpg/ jpeg, png 
                      </span>
                      </>
                    )}
                  </span>
                </span>
              </label>
            </div>
          )}
        </Dropzone>
        {error && (
          <div className="text-danger mt-2 font-sm">
            {error}
          </div>
        )}
      </>
    );
  } else if (isUploading) {
    return (
      <div
        className={classnames({
          'upload p-3 rounded text-center font-normal uploading w-100 m-0 cursor-pointer d-flex align-items-center justify-content-center': true,
          [props.className || '']: true,
        })}
      >
        <div
          className="uploadfile__progress-bar"
          style={{ width: `${(progress || 0) * 100}%` }}
        ></div>
        <span className="d-block">
          <i className="icon-upload font-xxl"></i>
          <span className="default-upload-label">
            <span className="d-block font-weight-semibold mt-2 py-1">
              {props.title}
            </span>
            <span className="d-block text-gray-500 text-decoration-underline">
              Drag the file here. Or Click here
            </span>
          </span>
        </span>
      </div>
    );
  } else if (mediaFile?.originalname) {
    return (
      <div
        className={classnames({
          'upload p-3 rounded text-center font-normal uploading w-100 m-0 cursor-pointer d-flex align-items-center justify-content-center position-relative': true,
          [props.className || '']: true,
        })}
      >
        <div className="uploadfile__icon px-2">{mediaFile?.originalname}</div>
        {!props.disabled && (
          <Button
            type="button"
            btnlink
            onClick={closeFile}
            className="uploadfile__closes"
          >
            <span className="uploadfile__close">
              <i className="icon-close uploadfile__close-icon"></i>
            </span>
          </Button>
        )}
      </div>
    );
  }
  return <div></div>;
};

export default FileUpload;
